<script setup lang="ts">
import { useI18n } from "vue-i18n"
import { computed, ref, watch } from "vue"
import { watchOnce } from "@vueuse/core"
import { useSourceStore } from "@core/stores/source.store"
import { SOURCES_TYPES, OFFER_NAMES, GRID_OFFER_CODES } from "@core/constants"
import { isNegativeValue, minValue } from "@core/helpers"
import { TypeOfferForm } from "@types"

const { t, tm } = useI18n({
    messages: {
        fr: {
            grille_tarifaire_options: [
                { name: "Base", value: "Base" },
                { name: "HP/HC", value: "HPHC" },
                { name: "Tempo", value: "Tempo" },
                { name: "Autre", value: "Autre" },
            ],
            hors_taxe: "* Hors toutes taxes",
            saison_haute_hphc: "Tarif saison haute  HP/HC HTT*",
            saison_basse_hphc: "Tarif saison basse HP/HC HTT*",
            blue_days: "Jours bleus",
            white_days: "Jours blancs",
            red_days: "Jours rouges",
            hphc: "Tarif HP/HC HTT*",
            hp: "Tarif heures pleines",
            hc: "Tarif heures creuses",
            base: "Tarif base HTT*",
            moyen: "Tarif moyen du kWh HTT*",
            prix_abonnement: "Tarif d'abonnement HTT*",
            unit: "centimes/kWh",
            cta: "Contribution Tarifaire d'Acheminement (CTA) annuelle",
            help_message:
                "Veuillez noter que tous les tarifs en kWh doivent être renseignés en centimes d'euros. Merci de vérifier attentivement la facture du fournisseur.",
        },
    },
})

type Props = {
    meterId: string
    type: string
    value: object
}
const props = defineProps<Props>()

const sourceStore = useSourceStore()

// ================================
// COMPUTED PROPERTIES
// ================================

/**
 * Computed pour récupérer les infos de la source
 */
const getSourceById = computed(() => {
    return sourceStore.getSourceById(props.meterId)
})

/**
 * Récupère le segment du compteur à partir des informations de la source
 */
const getSegment = computed(() => {
    const segment = ""
    if (getSourceById?.value?.informations?.pdl) {
        return getSourceById?.value?.informations?.pdl?.compteur?.segment
    }
    return segment
})

/**
 * Récupère le nom de l'offre à partir des informations de la source
 */
const getOfferName = computed(() => {
    const offer = ""
    if (getSourceById?.value?.informations?.pdl) {
        return getSourceById?.value?.informations?.pdl?.structure_tarifaire
            ?.calendrier_fournisseur?.libelle
    }
    return offer
})

/**
 * Les props communes à tous les inputs
 */
const COMMUNS_INPUT_PROPS = {
    rules: [
        (value: number) =>
            minValue(value, 0, {
                name: "La valeur",
                suffix: "€",
                female: true,
            }),
    ],
    min: 0,
    step: 0.00001,
    minFractionDigits: 0,
    maxFractionDigits: 7,
    type: "number",
    locale: "fr-FR",
    required: true,
}
const COMMUNS_INPUT_EMIT = {
    error: (value: number | string | boolean, fieldName: string) => {
        inputErrors.value = {
            ...inputErrors.value,
            [fieldName]: value,
        }
    },
}

const emit = defineEmits(["update:form"])
/**
 * Formulaire de tarification
 */
const form = ref({
    // prix de l'abonnement
    prixAbonnement: null,
    // tarif base (catégorie Base)
    tarifBase: null,
    // tarif moyen (catégorie Autre)
    tarifMoyen: null,
    // Contribution Tarifaire d'Acheminement (CTA) annuelle
    cta_annuelle: null,
    // tarif heures pleines / creuses (catégorie HP/HC C5)
    hphc: {
        // tarif heures pleines
        tarifHP: null,
        // tarif heures creuses
        tarifHC: null,
    },
    // tarif heures pleines / creuses - haute / basse saison (catégorie HP/HC C4)
    hphcC4: {
        // tarif heures pleines haute saison
        tarifHPH: null,
        // tarif heures creuses haute saison
        tarifHCH: null,
        // tarif heures pleines basse saison
        tarifHPB: null,
        // tarif heures creuses basse saison
        tarifHCB: null,
    },
    // tarif tempo (catégorie Tempo)
    tempo: {
        // tarif tempo jour bleu heures pleines
        tarifBUHP: null,
        // tarif tempo jour bleu heures creuses
        tarifBUHC: null,
        // tarif tempo jour blanc heures pleines
        tarifBCHP: null,
        // tarif tempo jour blanc heures creuses
        tarifBCHC: null,
        // tarif tempo jour rouge heures pleines
        tarifRHP: null,
        // tarif tempo jour rouge heures creuses
        tarifRHC: null,
    },
})

// grille tarifaire sélectionnée (Base, HPHC, Tempo, Autre)
const grilleTarifaire = ref<TypeOfferForm | null>(null)

// pour chaque input, on stocke les erreurs de validation (vrai si erreur, faux sinon)
const inputErrors = ref({})

/**
 * Permet de récupérer le bon sous-formulaire en fonction de la grille tarifaire sélectionnée
 * @returns le formulaire correspondant à la grille tarifaire sélectionnée
 */
const getFormFromGrilleTarifaire = computed(() => {
    switch (grilleTarifaire.value) {
        case GRID_OFFER_CODES.BASE: {
            return {
                tarifBase: form.value.tarifBase,
                prixAbonnement: form.value.prixAbonnement,
            }
        }
        case GRID_OFFER_CODES.HPHC: {
            return {
                ...(getSegment.value === "C5"
                    ? form.value.hphc
                    : form.value.hphcC4),
                prixAbonnement: form.value.prixAbonnement,
            }
        }
        case GRID_OFFER_CODES.TEMPO: {
            return {
                ...form.value.tempo,
                prixAbonnement: form.value.prixAbonnement,
            }
        }
        default: {
            if (props.type === SOURCES_TYPES.PCE) {
                return {
                    tarifMoyen: form.value?.tarifMoyen,
                    prixAbonnement: form.value.prixAbonnement,
                    cta_annuelle: form.value.cta_annuelle,
                }
            } else
                return {
                    tarifMoyen: form.value?.tarifMoyen,
                    prixAbonnement: form.value.prixAbonnement,
                }
        }
    }
})

/**
 * Valide le formulaire en fonction de la grille tarifaire sélectionnée et du type de compteur
 * @returns true si le formulaire est valide
 */
const isValidForm = computed(() => {
    // valider le formulaire en fonction de l'offre sélectionnée

    let validPricingForm

    // en fonction de la grille tarifaire on vérifie le bon sous formulaire
    switch (grilleTarifaire.value) {
        // cas pour une offre HPHC
        case GRID_OFFER_CODES.HPHC: {
            // si c'est un C5 on vérifie le formulaire dans form.hphc
            if (getSegment.value === "C5")
                validPricingForm = Object.values(form.value.hphc).every(
                    (value) => !isNegativeValue(value)
                )
            // sinon on vérifie le formulaire dans form.hphcC4
            else
                validPricingForm = Object.values(form.value.hphcC4).every(
                    (value) => !isNegativeValue(value)
                )
            break
        }

        // cas pour une offre Tempo
        case GRID_OFFER_CODES.TEMPO: {
            validPricingForm = Object.values(form.value.tempo).every(
                (value) => !isNegativeValue(value)
            )
            break
        }
        // cas pour une offre Base
        case GRID_OFFER_CODES.BASE: {
            validPricingForm = !isNegativeValue(form.value.tarifBase)
            break
        }
        // tous les autres cas d'offres (Autre)
        default: {
            validPricingForm = !isNegativeValue(form.value.tarifMoyen)
            break
        }
    }
    // on vérifie si dans le dict inputErrors, il n'y a pas d'erreurs
    const noInputErrors = Object.values(inputErrors.value).every(
        (value) => !value
    )
    // on vérifie que le prix de l'abonnement est valide
    return (
        validPricingForm &&
        !isNegativeValue(form.value.prixAbonnement) &&
        noInputErrors
    )
})

/**
 * Met à jour la grille tarifaire en fonction de l'offre renvoyée par Enedis ou GRDF
 */
const updatePricingGridByOfferName = (value: string) => {
    switch (value) {
        case OFFER_NAMES.BASE: {
            grilleTarifaire.value = GRID_OFFER_CODES.BASE
            break
        }
        case OFFER_NAMES.HPHC: {
            grilleTarifaire.value = GRID_OFFER_CODES.HPHC
            break
        }
        case OFFER_NAMES.TEMPO: {
            grilleTarifaire.value = GRID_OFFER_CODES.TEMPO
            break
        }
        default: {
            // si c'est un compteur PCE, on prend la grille tarifaire Autre
            if (props.type === SOURCES_TYPES.PCE) {
                grilleTarifaire.value = GRID_OFFER_CODES.OTHER
                break
            }
            // si le tarif base est rempli, on prend la grille tarifaire Base
            if (form.value.tarifBase !== null)
                grilleTarifaire.value = GRID_OFFER_CODES.BASE
            // si le tarif hphc ou hphcC4 est rempli, on prend la grille tarifaire HPHC
            else if (
                form.value.hphc.tarifHC !== null ||
                form.value.hphcC4.tarifHPH !== null
            )
                grilleTarifaire.value = GRID_OFFER_CODES.HPHC
            else if (form.value.tempo.tarifBUHC !== null)
                grilleTarifaire.value = GRID_OFFER_CODES.TEMPO
            else grilleTarifaire.value = GRID_OFFER_CODES.OTHER
            break
        }
    }
}

// quand l'offre change, on met à jour la grille tarifaire par défaut
watch(
    () => getOfferName.value,
    (value) => {
        updatePricingGridByOfferName(value || "")
    },
    {
        immediate: true,
    }
)

// quand le formulaire devient valide ou non, on émet un événement
watch(
    form,
    () => {
        emit("update:form", {
            form: getFormFromGrilleTarifaire.value,
            typeOffer: grilleTarifaire.value,
        })
    },
    {
        deep: true,
    }
)

/**
 * Options possibles de grille tarifaire
 * Si on a un C4 alors on ne peut pas choisir l'option "Base" et "Tempo"
 * @returns liste des options possibles pour les grilles tarifaires
 */
const grilleTarifaireOptions = computed(() => {
    if (getSegment.value === "C4") {
        return tm("grille_tarifaire_options").filter(
            (option: { name: string; value: string }) =>
                [GRID_OFFER_CODES.HPHC, GRID_OFFER_CODES.OTHER].includes(
                    option.value as TypeOfferForm
                )
        )
    }
    return tm("grille_tarifaire_options")
})

watchOnce(
    () => props.value,
    (value) => {
        form.value = {
            ...form.value,
            ...value,
        }
        updatePricingGridByOfferName(getOfferName.value || "")
    },
    {
        immediate: true,
    }
)
watch(
    form,
    () => {
        emit("update:form", {
            form: getFormFromGrilleTarifaire.value,
            typeOffer: grilleTarifaire.value,
        })
    },
    {
        deep: true,
    }
)

defineExpose({
    valid: isValidForm,
})
</script>

<template>
    <div class="form-source-pricing flex flex-col gap-6">
        <!-- CTA -->
        <div
            v-if="type === SOURCES_TYPES.PCE"
            class="w-full flex flex-col gap-2"
        >
            <BaseInputWrapper
                input-id="cta"
                :label="t('cta')"
                unfloat
                suffix="€/an"
            >
                <BaseInputNumber
                    id="cta"
                    v-model="form.cta_annuelle"
                    input-id="cta_annuelle"
                    mode="decimal"
                    v-bind="COMMUNS_INPUT_PROPS"
                    class="w-full"
                />
            </BaseInputWrapper>
        </div>
        <!-- Prix abonnement -->
        <div class="w-full flex flex-col gap-2">
            <BaseInputWrapper
                input-id="prix_abonnement"
                :label="t('prix_abonnement')"
                unfloat
                suffix="€/mois"
            >
                <BaseInputNumber
                    input-id="prix_abonnement"
                    v-model="form.prixAbonnement"
                    v-bind="COMMUNS_INPUT_PROPS"
                    v-on="COMMUNS_INPUT_EMIT"
                />
            </BaseInputWrapper>
        </div>
        <!-- Choix des différents types d'offres dans le cas ou on ne connait pas l'offre ou si c'est un C4-->
        <div
            v-if="
                !Object.values(OFFER_NAMES).includes(getOfferName || '') &&
                type === SOURCES_TYPES.PDL
            "
            class=""
        >
            <BaseRadioButtonGroup
                v-model="grilleTarifaire"
                name="grille-tarifaire"
                class="flex flex-row gap-6"
            >
                <div
                    v-for="(grille, index) in grilleTarifaireOptions"
                    :key="index"
                    class="flex flex-row gap-2"
                >
                    <BaseRadioButton
                        :value="grille.value"
                        :input-id="`grille-tarifaire-${index}`"
                    />
                    <label>{{ grille.name }}</label>
                </div>
            </BaseRadioButtonGroup>
        </div>
        <!-- Heures pleines / Heures creuses pour C4 -->
        <div
            v-if="
                grilleTarifaire === GRID_OFFER_CODES.HPHC && getSegment === 'C4'
            "
            class="flex flex-col gap-8"
        >
            <!-- tarif heures creuses/pleines haute saison -->
            <fieldset class="grid grid-cols-1 gap-4 md:grid-cols-2">
                <legend>
                    {{ t("saison_haute_hphc") }}
                </legend>
                <BaseInputWrapper
                    input-id="hph"
                    :label="t('hp')"
                    unfloat
                    :suffix="t('unit')"
                >
                    <BaseInputNumber
                        v-model="form.hphcC4.tarifHPH"
                        v-bind="COMMUNS_INPUT_PROPS"
                        input-id="hph"
                        v-on="COMMUNS_INPUT_EMIT"
                    ></BaseInputNumber>
                </BaseInputWrapper>
                <BaseInputWrapper
                    input-id="hpc"
                    :label="t('hc')"
                    unfloat
                    :suffix="t('unit')"
                >
                    <BaseInputNumber
                        input-id="hpc"
                        v-model="form.hphcC4.tarifHCH"
                        v-bind="COMMUNS_INPUT_PROPS"
                        v-on="COMMUNS_INPUT_EMIT"
                    ></BaseInputNumber>
                </BaseInputWrapper>
            </fieldset>
            <!-- tarif heures creuses/pleines basse saison -->
            <fieldset class="grid grid-cols-1 gap-4 md:grid-cols-2">
                <legend class="cols-span-2">
                    {{ t("saison_basse_hphc") }}
                </legend>
                <BaseInputWrapper
                    input-id="hpb"
                    :label="t('hp')"
                    unfloat
                    :suffix="t('unit')"
                >
                    <BaseInputNumber
                        input-id="hpb"
                        v-model="form.hphcC4.tarifHPB"
                        v-bind="COMMUNS_INPUT_PROPS"
                        v-on="COMMUNS_INPUT_EMIT"
                    ></BaseInputNumber>
                </BaseInputWrapper>
                <BaseInputWrapper
                    input-id="hcb"
                    :label="t('hc')"
                    unfloat
                    :suffix="t('unit')"
                >
                    <BaseInputNumber
                        input-id="hcb"
                        v-model="form.hphcC4.tarifHCB"
                        v-bind="COMMUNS_INPUT_PROPS"
                        v-on="COMMUNS_INPUT_EMIT"
                    ></BaseInputNumber>
                </BaseInputWrapper>
            </fieldset>
        </div>
        <!-- Heures pleines / Heures creuses pour les autres types de compteurs -->
        <div
            v-else-if="grilleTarifaire === GRID_OFFER_CODES.HPHC"
            class="form-source-pricing__sub-form"
        >
            <!-- tarif heures creuses/pleines pour les C5 -->
            <fieldset class="grid grid-cols-1 gap-4 md:grid-cols-2">
                <legend>
                    {{ t("hphc") }}
                </legend>
                <BaseInputWrapper
                    input-id="hp"
                    :label="t('hp')"
                    unfloat
                    :suffix="t('unit')"
                >
                    <BaseInputNumber
                        input-id="hp"
                        v-model="form.hphc.tarifHP"
                        v-bind="COMMUNS_INPUT_PROPS"
                        v-on="COMMUNS_INPUT_EMIT"
                    ></BaseInputNumber>
                </BaseInputWrapper>
                <BaseInputWrapper
                    input-id="hc"
                    :label="t('hc')"
                    unfloat
                    :suffix="t('unit')"
                >
                    <BaseInputNumber
                        input-id="hc"
                        v-model="form.hphc.tarifHC"
                        v-bind="COMMUNS_INPUT_PROPS"
                        v-on="COMMUNS_INPUT_EMIT"
                    ></BaseInputNumber>
                </BaseInputWrapper>
            </fieldset>
        </div>
        <!-- Tarif Tempo -->
        <div
            v-else-if="grilleTarifaire === GRID_OFFER_CODES.TEMPO"
            class="w-full flex flex-col gap-4"
        >
            <!-- jours bleus -->
            <fieldset class="grid grid-cols-1 gap-4 md:grid-cols-2">
                <legend>
                    {{ t("blue_days") }}
                </legend>
                <BaseInputWrapper
                    input-id="tarifBUHP"
                    :label="t('hp')"
                    unfloat
                    :suffix="t('unit')"
                >
                    <BaseInputNumber
                        input-id="tarifBUHP"
                        v-model="form.tempo.tarifBUHP"
                        v-bind="COMMUNS_INPUT_PROPS"
                        v-on="COMMUNS_INPUT_EMIT"
                    />
                </BaseInputWrapper>
                <BaseInputWrapper
                    input-id="tarifBUHC"
                    :label="t('hc')"
                    unfloat
                    :suffix="t('unit')"
                >
                    <BaseInputNumber
                        input-id="tarifBUHC"
                        v-model="form.tempo.tarifBUHC"
                        v-bind="COMMUNS_INPUT_PROPS"
                        v-on="COMMUNS_INPUT_EMIT"
                    />
                </BaseInputWrapper>
            </fieldset>
            <!-- jours blanc -->
            <fieldset class="grid grid-cols-1 gap-4 md:grid-cols-2">
                <legend>
                    {{ t("white_days").toString() }}
                </legend>
                <BaseInputWrapper
                    input-id="tarifBCHP"
                    :label="t('hp')"
                    unfloat
                    :suffix="t('unit')"
                >
                    <BaseInputNumber
                        input-id="tarifBCHP"
                        v-model="form.tempo.tarifBCHP"
                        v-bind="COMMUNS_INPUT_PROPS"
                        v-on="COMMUNS_INPUT_EMIT"
                    />
                </BaseInputWrapper>
                <BaseInputWrapper
                    input-id="tarifBCHC"
                    :label="t('hc')"
                    unfloat
                    :suffix="t('unit')"
                >
                    <BaseInputNumber
                        input-id="tarifBCHC"
                        v-model="form.tempo.tarifBCHC"
                        v-bind="COMMUNS_INPUT_PROPS"
                        v-on="COMMUNS_INPUT_EMIT"
                    />
                </BaseInputWrapper>
            </fieldset>
            <!-- jours rouges -->
            <fieldset class="grid grid-cols-1 gap-4 md:grid-cols-2">
                <legend>
                    {{ t("red_days").toString() }}
                </legend>
                <BaseInputWrapper
                    input-id="tarifRHP"
                    :label="t('hp')"
                    unfloat
                    :suffix="t('unit')"
                >
                    <BaseInputNumber
                        id="tarifRHP"
                        v-model="form.tempo.tarifRHP"
                        v-bind="COMMUNS_INPUT_PROPS"
                        v-on="COMMUNS_INPUT_EMIT"
                    />
                </BaseInputWrapper>
                <BaseInputWrapper
                    input-id="tarifRHC"
                    :label="t('hp')"
                    unfloat
                    :suffix="t('unit')"
                >
                    <BaseInputNumber
                        input-id="tarifRHC"
                        v-model="form.tempo.tarifRHC"
                        v-bind="COMMUNS_INPUT_PROPS"
                        v-on="COMMUNS_INPUT_EMIT"
                    />
                </BaseInputWrapper>
            </fieldset>
        </div>
        <!-- Tarif base -->
        <div
            v-else-if="
                getOfferName === OFFER_NAMES.BASE ||
                grilleTarifaire === GRID_OFFER_CODES.BASE
            "
            class="flex flex-col gap-2"
        >
            <BaseInputWrapper
                input-id="tarifBase"
                :label="t('base')"
                unfloat
                :suffix="t('unit')"
            >
                <BaseInputNumber
                    input-id="tarifBase"
                    v-model="form.tarifBase"
                    v-bind="COMMUNS_INPUT_PROPS"
                    v-on="COMMUNS_INPUT_EMIT"
                />
            </BaseInputWrapper>
        </div>
        <!-- Tarif moyen -->
        <div
            v-if="
                type === SOURCES_TYPES.PCE ||
                grilleTarifaire === GRID_OFFER_CODES.OTHER
            "
            class="flex flex-col gap-2"
        >
            <BaseInputWrapper
                input-id="tarifMoyen"
                :label="t('moyen')"
                unfloat
                :suffix="t('unit')"
            >
                <BaseInputNumber
                    input-id="tarifMoyen"
                    v-model="form.tarifMoyen"
                    v-bind="COMMUNS_INPUT_PROPS"
                    v-on="COMMUNS_INPUT_EMIT"
                />
            </BaseInputWrapper>
        </div>
    </div>
</template>

<style scoped></style>
